/* eslint-disable no-alert */
import { useLevels } from '@studio/design-system/audio/levels';
import { Meter, plotDecibelValue } from '@studio/design-system/audio/meter';
import * as Select from '@studio/design-system/select';
import { ensureNotNil } from '@studio/js-utils';
import { amplitudeToDb } from '@studio/react-utils/audio';
import { χref } from '@studio/react-utils/hooks';
import { noop } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { Column, Exhibit } from './styled.jsx';
import { Volume } from '#~/audio/components/volume/index.js';
import { useMutedPref, useVolumePref } from '#~/audio/hooks.js';
import { useResumeAudio } from '#~/audio/sound/hooks.js';
import { getMediaElementSource, resume, sound } from '#~/audio/sound/index.js';

const formatValue = (value: number): ReactElement => (
  <span className='font-mono'>
    {value !== 0
      ? `${value.toFixed(2)} / ${Math.round(amplitudeToDb(value))
          .toString()
          .padStart(3, '\u00A0')} dB`
      : '\u2014'}
  </span>
);

const isMicrophone = (source: string): boolean => source === 'microphone';

const Content = (args: Parameters<typeof useLevels>[0]): ReactElement => {
  const levels = useLevels({ ...args, weighted: true });

  return (
    <>
      <Meter value={plotDecibelValue(amplitudeToDb(levels.peak))} />

      <div className='grid grid-cols-2 gap-8'>
        <div>Peak</div>
        <div>{formatValue(levels.weightedPeak)}</div>

        <div>RMS</div>
        <div>{formatValue(levels.weightedRms)}</div>
      </div>
    </>
  );
};

/** `<Audio>` exhibit. */
export const Audio = (): ReactElement => {
  const [source, setSource] = useState('sample');
  const [playing, setPlaying] = useState(false);

  const [volume, setVolume] = useVolumePref();
  const [muted, setMuted] = useMutedPref();

  const [audioNode, setAudioNode] = useState<AudioNode>();
  const [microphoneNode, setMicrophoneNode] = useState<AudioNode>();

  const audioRef = useRef<HTMLAudioElement>(null);

  // Deal with audio security policy.
  useResumeAudio();

  // If playing media, keep the `<audio>` element volume in sync with master fader.
  useEffect(() => {
    const audioElement = χref(audioRef);

    if (!audioElement) {
      return;
    }

    audioElement.volume = volume;
  }, [volume]);

  // Sync selected source.
  useEffect(() => {
    const { context, master } = sound;

    // Are we using the microphone?
    if (isMicrophone(source)) {
      // Connect the microphone to the master fader.
      navigator.mediaDevices
        .getUserMedia({
          audio: {
            autoGainControl: false,
            echoCancellation: false,
            noiseSuppression: false
          },
          video: false
        })
        .then((stream) => {
          const microphone = context.createMediaStreamSource(stream);

          // Connect the microphone to the master fader.
          microphone.connect(master);

          setMicrophoneNode(microphone);
        })
        .catch((error) => {
          console.error('Error accessing microphone:', error);
        });

      // On cleanup, disconnect the microphone.
      return () => {
        setMicrophoneNode((µ) => {
          µ?.disconnect();

          return undefined;
        });
      };
    }

    // We are using a pre-recorded sample.
    const audioElement = ensureNotNil(χref(audioRef));

    // Connect the audio element to the master fader.
    const node = getMediaElementSource(context, audioElement);

    node.connect(master);
    setAudioNode(node);

    // There is nothing we really need to do here other than return a cleanup function. */
    return () => {
      // Stop the music.
      setPlaying(false);

      // Disconnect the existing `audio` source.
      setAudioNode(undefined);
    };
  }, [source]);

  const { context } = sound;

  return (
    <Exhibit title='Audio'>
      <Column>
        <div className='font-bold'>Decibel Meter</div>

        <Select.Root onValueChange={setSource} value={source}>
          <Select.Trigger>
            <Select.Value />
          </Select.Trigger>

          <Select.Content>
            <Select.Item value='sample'>Music Sample</Select.Item>
            <Select.Item value='dtmf'>DTMF Tones</Select.Item>
            <Select.Item value='noise'>White Noise</Select.Item>
            <Select.Item value='1k-10db'>Tone 1KHz @ -10dB</Select.Item>
            <Select.Item value='1k-5db'>Tone 1KHz @ -5dB</Select.Item>
            <Select.Item value='microphone'>Microphone</Select.Item>
          </Select.Content>
        </Select.Root>

        <Volume />

        {source !== 'microphone' ? (
          <>
            <audio
              className='w-full'
              controls
              id='test-audio'
              key={source}
              loop
              muted={muted}
              onPause={(): void => {
                setPlaying(false);
              }}
              onPlay={(): void => {
                resume(context)
                  .then(() => {
                    if (!playing) {
                      setPlaying(true);
                    }
                  })
                  .catch(noop);
              }}
              onVolumeChange={(event): void => {
                const target = event.currentTarget;

                setMuted(target.muted);
                setVolume(target.volume);
              }}
              preload='metadata'
              ref={audioRef}
            >
              <source src={`/assets/audio/${source}.mp3`} type='audio/mp3' />
            </audio>

            {!!audioNode && <Content audio={audioNode} context={context} disabled={!playing} />}
          </>
        ) : (
          !!microphoneNode && <Content audio={microphoneNode} context={context} />
        )}
      </Column>
    </Exhibit>
  );
};
